import { Table } from '@tanstack/react-table';
import { setUserSelectedColumns } from 'src/api';

/** Columns that stay fixed at the left and cannot be dragged. */
export function getPinnedColumnIds(defaultPinnedColumn: string): string[] {
  return ['select', defaultPinnedColumn].filter(Boolean);
}

/**
 * Move `draggedId` before or after `targetId` in the column order.
 * Pinned columns stay at the front in their original relative order.
 */
export function moveColumnInOrder(
  columnOrder: string[],
  draggedId: string,
  targetId: string,
  place: 'before' | 'after',
  pinnedIds: string[]
): string[] {
  if (
    !draggedId ||
    !targetId ||
    draggedId === targetId ||
    pinnedIds.includes(draggedId) ||
    pinnedIds.includes(targetId)
  ) {
    return columnOrder;
  }

  const withoutDragged = columnOrder.filter((id) => id !== draggedId);
  let insertAt = withoutDragged.indexOf(targetId);
  if (insertAt === -1) {
    return columnOrder;
  }
  if (place === 'after') {
    insertAt += 1;
  }
  withoutDragged.splice(insertAt, 0, draggedId);

  const pinned = columnOrder.filter((id) => pinnedIds.includes(id));
  const rest = withoutDragged.filter((id) => !pinnedIds.includes(id));
  return [...pinned, ...rest];
}

export function sameColumnOrder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

export function getOrderedVisibleColumnIds<T>(
  table: Table<T>,
  columnOrder: string[]
): string[] {
  const visibleColumnIds = table.getVisibleLeafColumns().map((col) => col.id);
  return [...visibleColumnIds].sort((a, b) => {
    const indexA = columnOrder.indexOf(a);
    const indexB = columnOrder.indexOf(b);
    return indexA - indexB;
  });
}

export async function persistVisibleColumnOrder<T>(
  table: Table<T>,
  columnOrder: string[],
  tableId: string
): Promise<string[]> {
  const orderedVisibleIds = getOrderedVisibleColumnIds(table, columnOrder);
  await setUserSelectedColumns(tableId, orderedVisibleIds);
  return orderedVisibleIds;
}
